import { useEffect, useState } from "react";
import DesktopMockup from "/src/assets/images/desktopmockup.png";

import { FullLogo } from "@/assets/images";
import { Coins, Newspaper, Repeat, Shield, Menu } from "lucide-react";
import { motion } from "framer-motion";
import { ReactSVG } from "react-svg";

import { FaCubes } from "react-icons/fa";
import GradientBorderButton from "@/components/ui/gradient-button";

export default function MoveToWebsite() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("home");

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const features = [
    {
      title: "Blockchain Real Estate Tools Coming Soon",
      description:
        "A suite of real estate–themed blockchain products is in development to reshape property investing.",
      icon: <FaCubes className="h-6 w-6 text-purple-400" />,
    },
    {
      title: "Our Native Token Live",
      description:
        "$RLCN powers the Real Chain ecosystem and enables seamless participation on Solana.",
      icon: <Coins className="h-6 w-6 text-purple-400" />,
    },
    {
      title: "Insights Column",
      description:
        "Stay updated with expert articles on blockchain, real estate, and market innovation.",
      icon: <Newspaper className="h-6 w-6 text-purple-400" />,
    },
    {
      title: "Liquidity and Convertibility",
      description:
        "Move between fiat, crypto, and $RLCN with full flexibility and low friction.",
      icon: <Repeat className="h-6 w-6 text-purple-400" />,
    },
    {
      title: "Security and Transparency",
      description:
        "All transactions are recorded on-chain and verified for trust and accountability.",
      icon: <Shield className="h-6 w-6 text-purple-400" />,
    },
  ];

  return (
    <div
      className={`mx-auto flex min-h-screen w-full flex-col overflow-hidden rounded-xl border border-gray-800 bg-black text-white shadow-2xl transition-opacity duration-1000 ${
        isVisible ? "opacity-100" : "opacity-0"
      }`}
    >
      {/* Header Navigation */}
      <header className="absolute left-0 right-0 top-0 z-20 p-4 md:p-6">
        <nav className="mx-auto flex max-w-7xl items-center justify-between gap-1">
          {/* Responsive Logo */}
          <div className="flex-shrink-0">
            <ReactSVG
              src={FullLogo}
              beforeInjection={(svg) => {
                svg.setAttribute("class", "w-[40vw] h-auto");
              }}
            />
          </div>

          {/* Tab Navigation */}
        </nav>
      </header>

      {/* Main Content Area */}
      <main className="flex flex-grow flex-col items-center pt-28 md:pt-28">
        {/* Home Tab Content */}
        {activeTab === "home" && (
          <motion.div
            className="flex w-full flex-col items-center px-4 text-center"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.h1
              className="mb-4 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-3xl font-bold text-transparent brightness-110 drop-shadow-[0_0_20px_rgba(59,130,246,0.5)] filter md:text-5xl lg:text-6xl"
              initial={{ backgroundPosition: "0% 50%" }}
              animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
              transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
              style={{ backgroundSize: "200% 200%" }}
            >
              Explore Our Desktop Platform
            </motion.h1>

            <p className="mb-8 max-w-3xl text-lg leading-relaxed text-gray-300 md:text-xl">
              Unlock the full potential of our features on our comprehensive
              desktop website, designed for a powerful and immersive experience.
            </p>

            <div className="relative mt-4 w-full max-w-sm md:max-w-md lg:max-w-3xl">
              <img
                src={DesktopMockup}
                alt="Website presentation on a desktop"
                className="h-auto w-full object-contain"
              />
              <div className="font-lg mx-auto mt-8 flex w-[70vw] items-center justify-center text-gray-300 sm:gap-6 md:gap-8">
                <GradientBorderButton
                  onClick={() => {
                    window.location.href = "/blogs";
                  }}
                  className="px-1 py-2 text-lg"
                  containerClass="justify-center "
                >
                  The Real Chain Column
                </GradientBorderButton>
              </div>
              <div className="pointer-events-none absolute -bottom-4 left-0 right-0 h-20 bg-gradient-to-t from-black to-transparent"></div>
            </div>
          </motion.div>
        )}

        {/* Features Tab Content */}
        {activeTab === "features" && (
          <motion.div
            id="features"
            className="mx-auto w-full max-w-6xl p-8"
            initial={{ opacity: 0, x: -80 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <h2 className="mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-center text-3xl font-bold text-transparent md:text-4xl">
              Platform Features
            </h2>

            <div className="grid grid-cols-1 gap-6 text-left md:grid-cols-2 lg:grid-cols-3">
              {features?.map((feature, index) => (
                <motion.div
                  key={index}
                  className="rounded-xl bg-gray-800 p-6 shadow-lg transition-shadow hover:shadow-purple-500/20"
                  whileHover={{ scale: 1.03 }}
                >
                  <div className="mb-3 flex items-center gap-4">
                    {feature?.icon}
                    <h3 className="text-xl font-semibold">{feature?.title}</h3>
                  </div>
                  <p className="text-gray-400">{feature?.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
